import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AllSystemState,
  getAllSystemState,
  getSystemJob,
  startAll,
  stopAll,
  SystemJob
} from 'src/Api/exchanges';
import { useSharedRefetchInterval } from 'src/Config/sharedRefetchInterval';

/**
 * Shared react-query key for `GET /api/system/all/state`. Exported so every
 * consumer (kill switch, per-domain presence) reads the SAME cache entry
 * instead of opening a second poll against the same endpoint.
 */
export const SYSTEM_ALL_STATE_KEY = 'system/all/state';
const ALL_STATE_KEY = SYSTEM_ALL_STATE_KEY;
const ALL_JOB_KEY = 'system/all/job';

/**
 * Self-contained data layer for the master "Stop All" kill switch.
 *
 * The per-subsystem buttons read job state from the module-scoped
 * `UpdatesProvider`, which is NOT mounted around the global SidebarNav — so the
 * kill-switch button gets its own react-query hook instead of `useUpdates()`:
 *  - aggregate state polled every 30s (one timer shared with every
 *    `useDomainPresence` reader of the same key),
 *  - the current job polled every 2s ONLY while it is running (the
 *    refetchInterval function form), seeded immediately by the stopAll
 *    mutation so the stepper renders without waiting for the first poll.
 */
export function useSystemKillSwitch() {
  const queryClient = useQueryClient();

  useSharedRefetchInterval([ALL_STATE_KEY], 30000);
  const stateQuery = useQuery<AllSystemState>(
    [ALL_STATE_KEY],
    getAllSystemState,
    { retry: 1 }
  );

  const jobQuery = useQuery<SystemJob | null>(
    [ALL_JOB_KEY],
    () => getSystemJob('predictions'),
    {
      // Poll fast while a job is running; stop once it settles.
      refetchInterval: job => (job?.status === 'running' ? 2000 : false),
      refetchOnWindowFocus: false,
      retry: 1
    }
  );

  const currentJob = jobQuery.data ?? null;

  // Refresh the aggregate state once a job transitions out of 'running'.
  const prevStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      prevStatus.current === 'running' &&
      currentJob &&
      currentJob.status !== 'running'
    ) {
      queryClient.invalidateQueries([ALL_STATE_KEY]);
    }
    prevStatus.current = currentJob?.status;
  }, [currentJob, queryClient]);

  // Seed the job cache so the stepper renders immediately, then let the 2s
  // refetchInterval take over until the job settles. Shared by both mutations.
  const onJobStarted = (job: SystemJob) => {
    queryClient.setQueryData([ALL_JOB_KEY], job);
    queryClient.invalidateQueries([ALL_JOB_KEY]);
  };

  const stopAllMutation = useMutation(stopAll, { onSuccess: onJobStarted });
  const startAllMutation = useMutation(startAll, { onSuccess: onJobStarted });

  return {
    isRunning: stateQuery.data?.isRunning ?? false,
    subsystems: stateQuery.data?.subsystems,
    currentJob,
    stopAll: stopAllMutation.mutate,
    startAll: startAllMutation.mutate,
    isStopping: stopAllMutation.isLoading,
    isStarting: startAllMutation.isLoading
  };
}
